// Edit Lease (SPEC.md, Module 5): the lessor, dates, term and the monthly
// amount (required; Dell leases carry no sales tax), budget line and notes.
// The summary shows the counted devices by OIC and the per-device share.

// REACT IMPORTS
import React, { useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { Autocomplete, Button, CircularProgress, Dialog, DialogContent, DialogTitle, InputAdornment, MenuItem, TextField } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// API, CONSTANTS
import { patchAPICall } from "../../../config/apiCalls";
import { apiError, fmtUSD } from "../itBudgetUtils";

const LESSORS = ["Dell Financial Services"];

const LeaseEditDialog = ({ lease, budgetItems, lessors, onClose, onSaved }) => {
	const { instance, accounts } = useMsal();
	const [form, setForm] = useState({
		lessor: lease.lessor || "",
		origin_date: lease.origin_date || "",
		term_date: lease.term_date || "",
		lease_length: lease.lease_length ?? "",
		lease_amount: lease.lease_amount ?? "",
		it_budget_item_id: lease.it_budget_item_id || "",
		notes: lease.notes || "",
	});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

	const amount = Number(form.lease_amount);
	const amountEntered = form.lease_amount !== "" && Number.isFinite(amount);
	const byOic = Object.entries(lease.devices_by_oic || {})
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([oic, n]) => `${oic} ${n}`)
		.join(" · ");

	const save = async () => {
		setSaving(true);
		setError("");
		try {
			await patchAPICall(instance, accounts[0], `/api/it-budget/leases/${lease.id}`, form);
			onSaved();
		} catch (err) {
			setError(apiError(err, "Couldn't save the lease."));
			setSaving(false);
		}
	};

	return (
		<Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
			<DialogTitle className="itBudgetDialogTitle">Edit Lease {lease.lease_number}</DialogTitle>
			<DialogContent className="itBudgetDialogContent">
				<div className="itBudgetFormGrid">
					<TextField size="small" label="Lease Number" value={lease.lease_number} disabled />
					<Autocomplete
						freeSolo
						options={[...new Set([...LESSORS, ...lessors])]}
						value={form.lessor}
						onInputChange={(e, value) => setForm((f) => ({ ...f, lessor: value }))}
						renderInput={(params) => <TextField {...params} size="small" label="Lessor" />}
					/>
				</div>
				<div className="itBudgetFormGrid">
					<TextField size="small" type="date" label="Start" value={form.origin_date} onChange={set("origin_date")} InputLabelProps={{ shrink: true }} />
					<TextField size="small" type="date" label="Term" value={form.term_date} onChange={set("term_date")} InputLabelProps={{ shrink: true }} />
					<TextField
						size="small"
						type="number"
						label="Lease Length"
						value={form.lease_length}
						onChange={set("lease_length")}
						InputProps={{ endAdornment: <InputAdornment position="end">months</InputAdornment> }}
					/>
				</div>

				<div className="itBudgetOverline">Payment</div>
				<div className="itBudgetFormGrid">
					<TextField
						size="small"
						required
						type="number"
						label="Lease Amount (monthly)"
						value={form.lease_amount}
						onChange={set("lease_amount")}
						inputProps={{ min: 0, step: "0.01" }}
						InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
					/>
					<TextField
						select
						size="small"
						label="Budget Line"
						value={form.it_budget_item_id}
						onChange={set("it_budget_item_id")}
						disabled={!budgetItems.length}
						helperText={budgetItems.length ? "" : "Available when the Annual Budget is set up"}
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
					>
						<MenuItem value="">None</MenuItem>
						{budgetItems.map((b) => (
							<MenuItem key={b.id} value={b.id}>
								{b.purpose}
							</MenuItem>
						))}
					</TextField>
				</div>
				<div className="itBudgetWarning" style={{ color: "#184c93" }}>
					<InfoOutlinedIcon fontSize="small" /> Dell leases carry no sales tax, so there is no tax field.
				</div>
				<TextField size="small" label="Notes" value={form.notes} onChange={set("notes")} multiline minRows={2} />

				<div className="itBudgetBanner" style={{ flexWrap: "wrap", gap: "6px 24px" }}>
					<span>
						Monthly <strong>{amountEntered ? fmtUSD(amount) : "—"}</strong>
					</span>
					<span>
						Counted devices <strong>{lease.device_count}</strong>
						{byOic && ` (${byOic})`}
					</span>
					<span>
						Per device <strong>{amountEntered && lease.device_count ? fmtUSD(Math.round((amount / lease.device_count) * 100) / 100) : "—"}</strong>
					</span>
					<span className="itBudgetMuted" style={{ flexBasis: "100%" }}>
						Feeds the From leases forecast and the expected Dell ACH payment (Phase 3). Shares are split to the cent, so they add up to the monthly amount.
					</span>
				</div>

				{error && <div className="itBudgetError">{error}</div>}
				<div className="itBudgetDialogButtons">
					<Button variant="outlined" color="error" onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button variant="contained" onClick={save} disabled={saving || !amountEntered} startIcon={saving ? <CircularProgress size={16} /> : null}>
						Save Lease
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default LeaseEditDialog;
